import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { LogOutIcon } from 'lucide-react';
import { cn, confirmDialog } from '@noor/lib';
import { useSignOut } from '../model/use-sign-out';

interface SignOutButtonProps {
  /** Runs before the confirm opens — the account popover closes itself. */
  onBeforeConfirm?: () => void;
  className?: string;
}

/**
 * Sign-out row for the header account popover. Styled as a popover menu item
 * (same 44px pill geometry as the Profile link beside it) rather than a
 * `Button`, and tinted `destructive` because it ends the session.
 *
 * The popover is dismissed via `onBeforeConfirm` before the confirm dialog
 * opens: leaving it mounted would put a focus trap behind the modal, and the
 * dialog's own outside-click would land on the popover underneath.
 */
export function SignOutButton({ onBeforeConfirm, className }: SignOutButtonProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const signOut = useSignOut(() => void navigate({ to: '/login' }));

  async function handleClick(): Promise<void> {
    onBeforeConfirm?.();
    await confirmDialog({
      title: t('auth.signOutConfirm.title'),
      description: t('auth.signOutConfirm.description'),
      confirmLabel: t('auth.signOutConfirm.confirm'),
      cancelLabel: t('auth.signOutConfirm.cancel'),
      destructive: true,
      onConfirm: () => signOut.mutateAsync(),
    });
  }

  return (
    <button
      type="button"
      disabled={signOut.isPending}
      onClick={() => void handleClick()}
      className={cn(
        'flex h-11 w-full items-center gap-3 rounded-[12px] px-3',
        'text-sm font-medium tracking-tight text-destructive',
        'cursor-pointer transition-colors outline-none',
        'hover:bg-destructive/10 focus-visible:bg-destructive/10',
        'disabled:pointer-events-none disabled:opacity-60',
        className,
      )}
    >
      <LogOutIcon className="size-5 shrink-0" />
      <span className="flex-1 text-left">
        {signOut.isPending ? t('auth.signingOut') : t('auth.signOut')}
      </span>
    </button>
  );
}
